import { RefObject, useCallback, useRef, useState } from "react";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import analytics from "web/script/analytics/analytics";

type ArticleAction = "clicked" | "impression";
type ArticleItemType = "editorial_content_card" | "editorial_content_block";

export interface EditorialArticleCardSchemaProps {
    item_type: ArticleItemType;
    component_type: string;
    editorial_label: string;
    franchise: string | null;
    editorial_gender: string;
    position: number;
    sponsored: boolean;
    content_type: string | "editorial";
    is_pinned: boolean;
    parent_area: string | "feeds";
}
export interface UseArticleAnalyticsProps {
    category: string;
    itemData: EditorialArticleCardSchemaProps[];
}

interface UseArticleAnalyticsResults {
    sendClickEvent: (area?: string, component_type?: string) => void;
    containerRef: RefObject<HTMLDivElement>;
}

export function useArticleAnalytics(params: UseArticleAnalyticsProps): UseArticleAnalyticsResults {
    const { category, itemData } = params;

    const containerRef = useRef<HTMLDivElement>(null);
    const { isVisible } = useAboveFold(containerRef, 0.5);
    const [hasImpression, setHasImpression] = useState(false);

    const sendAnalyticsEvent = useCallback(
        (action: ArticleAction, area?: string, component_type?: string) => {
            const updatedItemData =
                area || component_type
                    ? itemData.map((item) => ({
                          ...item,
                          ...(area && { area }),
                          ...(component_type && { component_type }),
                      }))
                    : itemData;

            analytics.event({
                category,
                action,
                nonInteraction: action !== "clicked",
                itemData: updatedItemData,
            });
        },
        [category, itemData]
    );

    useIsomorphicLayoutEffect(() => {
        if (isVisible && !hasImpression && itemData) {
            sendAnalyticsEvent("impression");
            setHasImpression(true);
        }
    }, [isVisible, hasImpression, itemData]);

    function sendClickEvent(area?: string, component_type?: string): void {
        sendAnalyticsEvent("clicked", area, component_type);
    }

    return { sendClickEvent, containerRef };
}
